import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SeasonData } from "./SeasonsData";
import { seasonDelete, seasonListApi } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { trimStr } from "../../utils/theme/ShortenedText/ShortenedText";
import ListView from "../../component/ListView/ListView";
import { contentStats, contentBadge } from "../../component/ListView/contentCard";
import { useSearch } from "../../context/SearchContext";
import { debounce } from "../../utils/debounce";

const Seasons = () => {
  const navigate = useNavigate();
  const { seriesId } = useParams();
  const [seasonData, setSeasonData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [episodeLoaderId, setEpisodeLoaderId] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedCreator, setSelectedCreator] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  const [selectedGenre, setSelectedGenre] = useState();
  const { searchQuery, setSearchQuery } = useSearch();
  const prevSearchQuery = useRef(searchQuery);
  const fetchData = useCallback(
    async (page, limit) => {
      setTableLoading(true);
      try {
        const data = await SeasonData(
          seriesId,
          selectedDate,
          selectedCreator,
          selectedRating,
          selectedGenre,
          page,
          limit
        );
        setSeasonData(data || []);
        // Always sync the total — an empty page (e.g. after deleting the last
        // item) must reset it to 0 rather than keep the stale count.
        setTotalCount(
          Array.isArray(data) && data.length > 0 ? data[0].totalCount || 0 : 0
        );
      } catch (err) {
        console.error("Error fetching seasons:", err);
        setSeasonData([]);
      } finally {
        setTableLoading(false);
      }
    },
    [seriesId]
  );

  const handleAddClick = () => {
    navigate(`/mainlayout/series/${seriesId}/seasons/add`);
  };

  const handleDelete = async (item) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this season?"
    );
    if (!confirmed) return;

    setDeletingId(item.seasonId);
    try {
      const response = await seasonDelete(seriesId, item.seasonId);
      setSeasonData((prev) => prev.filter((s) => s.seasonId !== item.seasonId));
      // Keep the Total tile in step without a refetch.
      setTotalCount((c) => Math.max(0, (Number(c) || 0) - 1));
      showToast(response.data.message, "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to delete season.",
        "error"
      );
    } finally {
      setDeletingId(null);
      fetchData();
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    navigate(`/mainlayout/series/${seriesId}/seasons/add/${item.seasonId}`, {
      state: { seasonData: item },
    });
  };

  const handleEpisodes = (item) => {
    localStorage.setItem("seriesId", seriesId);
    localStorage.setItem("seasonId", item.seasonId);
    setSearchQuery(null);
    setEpisodeLoaderId(item.seasonId);
    setTimeout(
      () =>
        navigate(
          `/mainlayout/series/${seriesId}/seasons/${item.seasonId}/episodes`
        ),
      300
    );
  };

  const cardFor = (item) => ({
    id: item.seasonId,
    thumbLabel: `S${item.season_number}`,
    title: item.name,
    meta: [item.rating, item.access].filter((v) => v && v !== "N/A").join(" · "),
    badge: contentBadge(item),
    onClick: () => handleEpisodes(item),
    actions: [
      { icon: "edit", label: "Edit", onClick: () => handleEdit(item) },
      {
        icon: "delete",
        label: "Delete",
        disabled: deletingId === item.seasonId,
        onClick: () => handleDelete(item),
      },
    ],
  });

  const handleFilterChange = async (type, value) => {
    if (type === "genre") setSelectedGenre(value);
    if (type === "date") setSelectedDate(value);
    if (type === "creator") setSelectedCreator(value);
    if (type === "rating") setSelectedRating(value);

    const updatedGenre = type === "genre" ? value : selectedGenre;
    const updatedDate = type === "date" ? value : selectedDate;
    const updatedCreator = type === "creator" ? value : selectedCreator;
    const updatedRating = type === "rating" ? value : selectedRating;

    setTableLoading(true);
    try {
      const response = await seasonListApi(
        seriesId,
        updatedDate,
        updatedCreator,
        updatedRating,
        updatedGenre,
        "",
        searchQuery
      );

      const data = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      const formattedData = data.map((item) => ({
        season_number: item.season_number,
        name: trimStr(item.name),
        media: item.portrait,
        status: item.status,
        qc_status: item.qc_status,
        rating: item.rating,
        portrait_url: item.portrait,
        landscape_url: item.landscape,
        access: item.access,
        buttonText: "Edit",
        buttonText2: "Remove",
        buttonText3: "Episodes",
        seasonId: item.season_id,
        seriesId: seriesId,
      }));

      setSeasonData(formattedData);
      setTotalCount(response?.data?.total);
      showToast();
    } catch (error) {
      showToast("Failed to apply filter", "error");
    } finally {
      setTableLoading(false);
    }
  };

  const debouncedFilterChange = useMemo(
    () => debounce(() => handleFilterChange(), 500),
    [selectedDate, selectedCreator, selectedRating, selectedGenre, searchQuery]
  );

  useEffect(() => {
    if (prevSearchQuery.current === searchQuery) {
      return;
    }
    prevSearchQuery.current = searchQuery;
    debouncedFilterChange();
  }, [searchQuery]);
  return (
    <div>
      <ListView
        heading="Seasons"
        data={seasonData}
        getCard={cardFor}
        stats={contentStats(seasonData, totalCount)}
        defaultView="rows"
        button={true}
        onPageChange={fetchData}
        tableLoading={tableLoading}
        handleFilterChange={handleFilterChange}
        selectedDate={selectedDate}
        selectedRating={selectedRating}
        selectedGenre={selectedGenre}
        totalCount={totalCount}
        addLabel="Add Season"
        onAdd={handleAddClick}
      />
    </div>
  );
};

export default Seasons;